import imageCompression from 'browser-image-compression';
import {Palette} from 'hubbl-shared';
import {memo, useEffect, useState, useRef} from 'react';
import {Control, Controller, useWatch} from 'react-hook-form';
import styled from 'styled-components';
import {FontConfig} from '../../constants/FontConfig';
import {useI18n} from '../../hooks';
import {FieldError} from './FieldError';
import {TextHeading7} from '../text/TextHeading7';

interface ImageSelectorInputProps {
  control: Control<any>;
  controlKey: string;
  isRequiredMessage?: string;
  onFileChange: (file: File) => void;
  externalLoading?: boolean;
}

export const ImageSelectorInput = memo(
  ({
    control,
    controlKey,
    isRequiredMessage,
    onFileChange,
    externalLoading = false,
  }: ImageSelectorInputProps) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const value = useWatch({control, name: controlKey});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const t = useI18n();

    useEffect(() => {
      if (value instanceof File) {
        if (value.size > 0) {
          const objectUrl = URL.createObjectURL(value);
          setPreviewUrl(objectUrl);
          // Cleanup the object URL when component unmounts or value changes
          return () => URL.revokeObjectURL(objectUrl);
        } else {
          setPreviewUrl(null);
        }
      } else if (typeof value === 'string' && value.startsWith('data:')) {
        setPreviewUrl(value);
      } else {
        setPreviewUrl(null);
      }
    }, [value]);

    const handleFileChange = async (file: File) => {
      try {
        setError(null);
        setIsLoading(true);

        // Validate file type
        if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
          throw new Error(t.scenes.createEvent.onlyImageFilesAllowed);
        }

        // Validate file size (e.g., max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(t.scenes.createEvent.fileSizeExceedsLimit);
        }

        // Compress the image
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.5, // Compress to 1MB
          maxWidthOrHeight: 2000, // Resize for large images
          useWebWorker: true,
        });

        // Generate preview
        const objectUrl = URL.createObjectURL(compressedFile);
        setPreviewUrl(objectUrl);

        // Create a new File from the compressed blob to ensure it has all File properties
        const finalFile = new File([compressedFile], file.name, {
          type: compressedFile.type,
          lastModified: file.lastModified,
        });

        onFileChange(finalFile); // Pass the final file back to parent form logic
        setIsLoading(false);
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? err.message
            : t.scenes.createEvent.failedToUploadImage,
        );
        setIsLoading(false);
      }
    };

    return (
      <Controller
        control={control}
        name={controlKey}
        rules={{required: isRequiredMessage}}
        render={({fieldState: {error: fieldError}}) => (
          <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            {!externalLoading && (
              <div
                style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                <FileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg, image/png, image/jpg"
                  onChange={e => {
                    if (e.target.files?.[0]) {
                      handleFileChange(e.target.files[0]);
                    }
                  }}
                />
                {value instanceof File && value.size > 0 && (
                  <div
                    style={{
                      fontSize: '12px',
                      color: Palette.almostBlack,
                      fontFamily: InputFontConfig.fontFamily,
                    }}>
                    {t.scenes.createEvent.imageSelected.replace(
                      '{filename}',
                      value.name,
                    )}
                  </div>
                )}
              </div>
            )}
            {isLoading || externalLoading ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 200,
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                  borderRadius: '20px',
                  border: `1px solid rgba(255, 255, 255, 0.3)`,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: `
                    0 4px 20px rgba(0, 0, 0, 0.08),
                    0 0 0 1px rgba(255, 255, 255, 0.2),
                    inset 0 1px 0 rgba(255, 255, 255, 0.8)
                  `,
                  backdropFilter: 'blur(12px)',
                }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    position: 'relative',
                    marginBottom: '16px',
                  }}>
                  <div
                    style={{
                      position: 'absolute',
                      width: '40px',
                      height: '40px',
                      border: `3px solid ${Palette.primaryCherokee}20`,
                      borderRadius: '50%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: '0s',
                      top: 0,
                      left: 0,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      width: '20px',
                      height: '20px',
                      top: 13,
                      left: 13,
                      background: `linear-gradient(45deg, ${Palette.primaryCherokee}, ${Palette.primaryTerraCotta})`,
                      borderRadius: '50%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: '0.75s',
                      boxShadow: `
                        0 0 12px ${Palette.primaryCherokee}40,
                        0 0 24px ${Palette.primaryCherokee}20
                      `,
                    }}
                  />
                </div>
                <TextHeading7
                  text={
                    externalLoading
                      ? t.ai.prompt.generatingCoverImage
                      : t.scenes.createEvent.loading
                  }
                  style={{
                    color: Palette.primaryCherokee,
                    fontWeight: '600',
                    letterSpacing: '0.3px',
                    fontSize: '14px',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background:
                      'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                    animation: 'shimmer 2s infinite',
                  }}
                />
                <style>
                  {`
                    @keyframes pulse {
                      0%, 100% {
                        transform: scale(1);
                        opacity: 1;
                      }
                      50% {
                        transform: scale(1.2);
                        opacity: 0.7;
                      }
                    }
                    @keyframes shimmer {
                      0% {
                        left: -100%;
                      }
                      100% {
                        left: 100%;
                      }
                    }
                  `}
                </style>
              </div>
            ) : previewUrl ? (
              <PreviewImage src={previewUrl} alt="Cover preview" />
            ) : (
              <DropZone
                isDragging={isDragging}
                onDragOver={e => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={e => {
                  e.preventDefault();
                  setIsDragging(false);
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    // Validate file type before processing
                    if (
                      !['image/jpeg', 'image/png', 'image/jpg'].includes(
                        file.type,
                      )
                    ) {
                      setError(t.scenes.createEvent.onlyImageFilesAllowed);
                      return;
                    }

                    // Update the file input's files property and dispatch a change event
                    if (fileInputRef.current) {
                      const dataTransfer = new DataTransfer();
                      dataTransfer.items.add(file);
                      fileInputRef.current.files = dataTransfer.files;
                      const event = new Event('change', {bubbles: true});
                      fileInputRef.current.dispatchEvent(event);
                    } else {
                      handleFileChange(file);
                    }
                  }
                }}
                onClick={() => fileInputRef.current?.click()}>
                {error ? (
                  <FieldError errorMessage={error} hasError={Boolean(error)} />
                ) : (
                  <TextHeading7 text={t.scenes.createEvent.dragAndDropImage} />
                )}
              </DropZone>
            )}
          </div>
        )}
      />
    );
  },
);
const InputFontConfig = FontConfig.Heading3;

const FileInput = styled.input`
  font-weight: ${() => InputFontConfig.fontWeight};
  font-family: ${() => InputFontConfig.fontFamily};
  font-size: 12px;
  padding: 10px; /* Adjust the padding to make the input larger */
  border-radius: 8px; /* Round the corners */
  border: 1px solid ${() => Palette.primaryBianca};
  color: ${() => Palette.almostBlack}; /* Text color inside the input */
  background-color: ${() => Palette.primarySubtleGreen}; /* Light background */
  cursor: pointer; /* Show pointer cursor */

  &:hover {
    border-color: #888; /* Change border color on hover */
  }

  &::file-selector-button {
    font-weight: ${() => InputFontConfig.fontWeight};
    font-family: ${() => InputFontConfig.fontFamily};
    font-size: 12px;
    padding: 10px 20px; /* Padding for the button */
    margin-right: 16px;
    background-color: ${() => Palette.primaryCherokee};
    color: ${() => Palette.almostBlack}; /* Text color of the button */
    border: none; /* Remove border */
    border-radius: 4px; /* Round the button corners */
    cursor: pointer; /* Pointer cursor on hover */
    transition: ease background-color 250ms;
    &:hover {
      background-color: ${() => Palette.primaryTerraCotta};
    }
  }

  &:focus {
    border-color: ${() => Palette.white};
    outline: none; /* Remove outline on focus */
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 200px;
  border-radius: 8px;
  object-fit: contain;
`;

const DropZone = styled.div<{isDragging: boolean}>`
  width: 100%;
  max-height: 200px;
  height: 200px;
  border: 2px dashed
    ${props => (props.isDragging ? Palette.primaryCherokee : '#ccc')};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props =>
    props.isDragging ? Palette.primaryCherokee : Palette.primarySubtleGreen};
  color: ${Palette.almostBlack};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  margin-bottom: 8px;
`;
